import { Timestamp } from 'firebase/firestore';
import { formatTimestamp } from './firebase';
import { cachedFirebase } from './cachedFirebase';
import { 
  doc as firestoreDoc, 
  getDoc,
  collection, 
  addDoc, 
  updateDoc, 
  query,
  where,
  orderBy,
  startAt,
  endAt,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { createOrUpdateClientAndBook } from './clients';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/queryClient';
import { 
  getAppointments, 
  checkTimeSlotAvailability, 
  getAvailableTimeSlots,
  getAllServices,
  getAllProfessionals,
} from './appointments';

/**
 * Busca agendamentos do salão com filtros opcionais (com cache)
 * @param {string} salonId - ID do salão
 * @param {object} filters - Filtros para a busca
 * @param {boolean} forceRefresh - Forçar atualização ignorando cache
 * @returns {Promise<Array>} - Lista de agendamentos
 */
export const getAppointmentsWithCache = async (salonId, filters = {}, forceRefresh = false) => {
  try {
    console.log("[getAppointments] Buscando agendamentos para o salão:", salonId, "Filtros:", filters);
    
    // Verificar se as datas dos filtros são objetos Date ou strings
    if (filters.startDate) {
      console.log(`[getAppointments] startDate é Date? ${filters.startDate instanceof Date}`, 
        filters.startDate instanceof Date ? 
          `${filters.startDate.toISOString()}` : 
          `Tipo: ${typeof filters.startDate}, Valor: ${filters.startDate}`
      );
    }
    
    if (filters.endDate) {
      console.log(`[getAppointments] endDate é Date? ${filters.endDate instanceof Date}`, 
        filters.endDate instanceof Date ? 
          `${filters.endDate.toISOString()}` : 
          `Tipo: ${typeof filters.endDate}, Valor: ${filters.endDate}`
      );
    }
    
    // Flag que indica se estamos usando strings para o formato de data
    const useStringDateFormat = filters.stringDateFormat === true;
    
    // Preparar condições where para o cachedFirebase
    const whereConditions = [];
    
    // Aplicar filtros se fornecidos
    if (filters.startDate) {
      whereConditions.push({
        field: 'data',
        operator: '>=',
        value: filters.startDate
      });
    }
    
    if (filters.endDate) {
      whereConditions.push({
        field: 'data',
        operator: '<=',
        value: filters.endDate
      });
    }
    
    if (filters.confirmed !== undefined) {
      whereConditions.push({
        field: 'confirmado',
        operator: '==',
        value: filters.confirmed
      });
    }
    
    if (filters.messagesSent !== undefined) {
      whereConditions.push({
        field: 'mensagemEnviada',
        operator: '==',
        value: filters.messagesSent
      });
    }
    
    if (filters.canceled !== undefined) {
      whereConditions.push({
        field: 'cancelado',
        operator: '==',
        value: filters.canceled
      });
    }
    
    // Definir ordenação
    const orderByFields = [{
      field: 'data',
      direction: useStringDateFormat ? 'asc' : 'desc'
    }];
    
    // Caminho da coleção
    const collectionPath = `salons/${salonId}/appointments`;
    
    // Opções de consulta
    const queryOptions = {
      whereConditions,
      orderByFields
    };
    
    // Estratégia de cache baseada no tipo de dados
    // - Para dados históricos (passado): mais estáticos, cache mais longo
    // - Para dados futuros (próximos dias): mais dinâmicos, cache mais curto
    let strategyType = 'dynamic';
    
    if (filters.startDate && filters.endDate) {
      const now = new Date();
      
      // Se ambas as datas estão no passado, usar cache mais longo
      if (new Date(filters.endDate) < now) {
        strategyType = 'static';
      }
      // Se estamos consultando dados de mais de 30 dias no futuro, também podemos usar cache mais longo
      else if (new Date(filters.startDate) > new Date(now.setDate(now.getDate() + 30))) {
        strategyType = 'static';
      }
    }
    
    // Opções de cache
    const cacheOptions = {
      forceRefresh,
      strategyType,
      transformResponse: async (data) => {
        if (!data) return [];
        
        // Processar timestamps
        const processedData = data.map(item => ({
          ...item,
          data: formatTimestamp(item.data)
        }));
        
        // Se não houver dados de serviços e profissionais incorporados,
        // buscamos esses dados (possivelmente do cache)
        const needsServiceDetails = processedData.some(
          appt => Array.isArray(appt.servicos) && 
          appt.servicos.some(service => typeof service === 'string')
        );
        
        const needsProfessionalDetails = processedData.some(
          appt => appt.profissional && typeof appt.profissional === 'string'
        );
        
        let servicesData = [];
        let professionalsData = [];
        
        // Buscar serviços e profissionais do cache, se necessário
        if (needsServiceDetails) {
          try {
            servicesData = await cachedFirebase.getCollection(
              `salons/${salonId}/services`, 
              {}, 
              { strategyType: 'reference' }
            );
          } catch (err) {
            console.error("Erro ao buscar serviços:", err);
          }
        }
        
        if (needsProfessionalDetails) {
          try {
            professionalsData = await cachedFirebase.getCollection(
              `salons/${salonId}/professionals`, 
              {}, 
              { strategyType: 'reference' }
            );
          } catch (err) {
            console.error("Erro ao buscar profissionais:", err);
          }
        }
        
        // Preencher detalhes faltantes
        return processedData.map(appointment => {
          const result = { ...appointment };
          
          // Substituir IDs de serviços por objetos completos
          if (Array.isArray(result.servicos) && needsServiceDetails) {
            result.servicos = result.servicos.map(serviceId => {
              if (typeof serviceId === 'string') {
                const service = servicesData.find(s => s.id === serviceId);
                return service || { id: serviceId, nome: 'Serviço desconhecido' };
              }
              return serviceId;
            });
          }
          
          // Substituir ID de profissional por objeto completo
          if (typeof result.profissional === 'string' && needsProfessionalDetails) {
            const professional = professionalsData.find(p => p.id === result.profissional);
            if (professional) {
              result.profissional = professional;
            }
          }
          
          return result;
        });
      }
    };
    
    // Buscar dados com cache
    return await cachedFirebase.getCollection(collectionPath, queryOptions, cacheOptions);
  } catch (error) {
    console.error('Erro ao buscar agendamentos:', error);
    throw error;
  }
};

/**
 * Busca agendamentos pendentes de confirmação (com cache)
 * @param {string} salonId - ID do salão
 * @param {number} daysAhead - Dias à frente para buscar
 * @param {boolean} forceRefresh - Forçar atualização ignorando cache
 * @returns {Promise<Array>} - Lista de agendamentos pendentes
 */
export const getPendingConfirmations = async (salonId, daysAhead = 7, forceRefresh = false) => {
  // Calcular datas para o filtro
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + daysAhead);
  endDate.setHours(23, 59, 59, 999);
  
  // Usar getAppointmentsWithCache com filtros apropriados
  return getAppointmentsWithCache(
    salonId, 
    { 
      startDate: today,
      endDate: endDate,
      confirmed: false,
      canceled: false,
      messagesSent: false
    },
    forceRefresh
  );
};

/**
 * Cria um novo agendamento e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {object} appointmentData - Dados do agendamento
 * @returns {Promise<object>} - Agendamento criado com ID
 */
export const createAppointment = async (salonId, appointmentData) => {
  try {
    // Utilizando a nova implementação com gerenciamento avançado de clientes
    const result = await createOrUpdateClientAndBook(salonId, appointmentData);
    
    if (result.success) {
      // Buscar o agendamento criado para retornar os dados completos
      const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${result.appointmentId}`);
      const appointmentSnap = await getDoc(appointmentRef);
      
      if (appointmentSnap.exists()) {
        return {
          id: result.appointmentId,
          clientId: result.clientId,
          ...appointmentData,
          ...appointmentSnap.data()
        };
      } else {
        return {
          id: result.appointmentId,
          clientId: result.clientId,
          ...appointmentData,
          confirmado: false,
          cancelado: false,
          mensagemEnviada: false
        };
      }
    } else {
      throw new Error("Falha ao criar agendamento: " + (result.error || "Erro desconhecido"));
    }
  } catch (error) {
    console.error('Erro ao criar agendamento:', error);
    throw error;
  }
};

/**
 * Confirma um agendamento e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se confirmado com sucesso
 */
export const confirmAppointment = async (salonId, appointmentId) => {
  try {
    const documentPath = `salons/${salonId}/appointments/${appointmentId}`;
    
    // Atualizar via Firebase padrão
    const appointmentRef = firestoreDoc(db, documentPath);
    await updateDoc(appointmentRef, {
      confirmado: true,
      dataConfirmacao: Timestamp.now()
    });
    
    // Invalidar cache do documento e da coleção
    cachedFirebase.invalidateCache(documentPath);
    
    return true;
  } catch (error) {
    console.error('Erro ao confirmar agendamento:', error);
    throw error;
  }
};

/**
 * Cancela um agendamento e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se cancelado com sucesso
 */
export const cancelAppointment = async (salonId, appointmentId) => {
  try {
    const documentPath = `salons/${salonId}/appointments/${appointmentId}`;
    
    // Atualizar via Firebase padrão
    const appointmentRef = firestoreDoc(db, documentPath);
    await updateDoc(appointmentRef, {
      confirmado: false,
      cancelado: true,
      dataCancelamento: Timestamp.now()
    });
    
    // Invalidar cache do documento e da coleção
    cachedFirebase.invalidateCache(documentPath);
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar agendamento:', error);
    throw error;
  }
};

/**
 * Marca um agendamento como tendo mensagem enviada e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se marcado com sucesso
 */
export const markAppointmentMessageSent = async (salonId, appointmentId) => {
  try {
    const documentPath = `salons/${salonId}/appointments/${appointmentId}`;
    
    // Atualizar via Firebase padrão
    const appointmentRef = firestoreDoc(db, documentPath);
    await updateDoc(appointmentRef, {
      mensagemEnviada: true,
      dataEnvioMensagem: Timestamp.now()
    });
    
    // Invalidar cache do documento e da coleção
    cachedFirebase.invalidateCache(documentPath);
    
    return true;
  } catch (error) {
    console.error('Erro ao marcar mensagem como enviada:', error);
    throw error;
  }
};

/**
 * Atualiza um agendamento existente e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @param {object} appointmentData - Novos dados do agendamento
 * @returns {Promise<object>} - Agendamento atualizado
 */
export const updateAppointment = async (salonId, appointmentId, appointmentData) => {
  try {
    const documentPath = `salons/${salonId}/appointments/${appointmentId}`;
    
    // Atualizar via Firebase padrão
    const appointmentRef = firestoreDoc(db, documentPath);
    await updateDoc(appointmentRef, {
      ...appointmentData,
      updatedAt: Timestamp.now()
    });
    
    // Invalidar cache do documento e da coleção
    cachedFirebase.invalidateCache(documentPath);
    
    return {
      id: appointmentId,
      ...appointmentData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Erro ao atualizar agendamento:', error);
    throw error;
  }
};

/**
 * Remove um agendamento e invalida o cache
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se removido com sucesso
 */
export const deleteAppointment = async (salonId, appointmentId) => {
  try {
    const documentPath = `salons/${salonId}/appointments/${appointmentId}`;
    
    // Excluir via Firebase padrão
    const appointmentRef = firestoreDoc(db, documentPath);
    await deleteDoc(appointmentRef);
    
    // Invalidar cache da coleção
    cachedFirebase.invalidateCache(`salons/${salonId}/appointments`);
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir agendamento:', error);
    throw error;
  }
};

/**
 * Hook para obter agendamentos com cache
 * @param {string} salonId - ID do salão 
 * @param {object} filters - Filtros para agendamentos
 * @param {object} options - Opções do React Query
 * @returns {object} - Resultado da query com cache
 */
export const useAppointments = (salonId, filters = {}, options = {}) => {
  const queryKey = [
    'appointments', 
    salonId, 
    filters.startDate, 
    filters.endDate,
    filters.confirmed,
    filters.canceled,
    filters.stringDateFormat
  ];
  
  return useQuery({
    queryKey,
    queryFn: () => getAppointmentsWithCache(salonId, filters),
    staleTime: 2 * 60 * 1000, // 2 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    ...options
  });
};

/**
 * Busca agendamentos com cache direto (não hook)
 * @param {string} salonId - ID do salão
 * @param {object} filters - Filtros para agendamentos 
 * @param {object} queryClient - Cliente React Query
 * @returns {Promise<Array>} - Lista de agendamentos
 */
export const fetchAppointmentsWithCache = async (salonId, filters = {}, queryClient) => {
  const queryKey = [
    'appointments', 
    salonId, 
    filters.startDate, 
    filters.endDate,
    filters.confirmed,
    filters.canceled,
    filters.stringDateFormat
  ];
  
  // Verificar se já existe em cache
  const cachedData = queryClient.getQueryData(queryKey);
  if (cachedData) {
    console.log(`[cachedAppointments] Usando cache para agendamentos de ${salonId} (${filters.startDate} - ${filters.endDate})`);
    return cachedData;
  }
  
  // Buscar dados e armazenar em cache
  const data = await getAppointmentsWithCache(salonId, filters);
  queryClient.setQueryData(queryKey, data);
  return data;
};

/**
 * Verificação de disponibilidade de slot com cache
 * @param {object} params - Parâmetros da verificação
 * @returns {object} - Resultado da query com cache
 */
export const useTimeSlotAvailability = ({ 
  salonId, 
  date, 
  time, 
  professionalId, 
  serviceIds, 
  excludeAppointmentId,
  enabled = true 
}) => {
  return useQuery({
    queryKey: [
      'timeSlotAvailability', 
      salonId, 
      date, 
      time, 
      professionalId, 
      serviceIds?.join(','), 
      excludeAppointmentId
    ],
    queryFn: () => checkTimeSlotAvailability(
      salonId, 
      date, 
      time, 
      professionalId, 
      serviceIds, 
      excludeAppointmentId
    ),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minuto
    cacheTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Obtenção de slots disponíveis com cache
 * @param {object} params - Parâmetros da consulta
 * @returns {object} - Resultado da query com cache
 */
export const useAvailableTimeSlots = ({ 
  salonId, 
  date, 
  professionalId, 
  serviceIds,
  enabled = true 
}) => {
  return useQuery({
    queryKey: [
      'availableTimeSlots', 
      salonId, 
      date, 
      professionalId, 
      serviceIds?.join(',')
    ],
    queryFn: () => getAvailableTimeSlots(
      salonId, 
      date, 
      professionalId, 
      serviceIds
    ),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });
};

/**
 * Obtenção de slots disponíveis com cache
 * 
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} professionalId - ID do profissional (opcional)
 * @param {Array} serviceIds - Array com IDs dos serviços selecionados
 * @param {object} queryClient - Instância do queryClient do React Query
 * @param {string} excludeAppointmentId - ID do agendamento a excluir da verificação (para remarcações)
 * @returns {Promise<Array>} - Lista de slots disponíveis
 */
export const fetchAvailableTimeSlotsWithCache = async (
  salonId,
  date,
  professionalId,
  serviceIds,
  queryClient,
  excludeAppointmentId = null
) => {
  console.log(`[fetchAvailableTimeSlotsWithCache] Iniciando com excludeAppointmentId=${excludeAppointmentId}`);
  
  console.log(`[fetchAvailableTimeSlotsWithCache] Parâmetros:`, {
    salonId,
    date,
    professionalId,
    serviceIds,
    excludeAppointmentId
  });
  
  // Validar parâmetros de entrada
  if (!salonId || !date || !serviceIds || !Array.isArray(serviceIds) || serviceIds.length === 0) {
    console.error(`[fetchAvailableTimeSlotsWithCache] Parâmetros inválidos`, {
      salonId,
      date,
      professionalId,
      serviceIds,
      excludeAppointmentId
    });
    return [];
  }
  
  // Se não temos um queryClient, fazer a chamada direta
  if (!queryClient) {
    return getAvailableTimeSlots(salonId, date, professionalId, serviceIds, excludeAppointmentId);
  }
  
  // Criar uma chave de cache
  const cacheKey = serviceIds.length > 0 
    ? [
        'availability',
        salonId,
        date,
        professionalId || 'all',
        serviceIds.sort().join('-'),
        excludeAppointmentId || 'none'
      ]
    : ['availability', salonId, date, professionalId || 'all', 'noServices', excludeAppointmentId || 'none'];
  
  console.log(`[fetchAvailableTimeSlotsWithCache] Chave de cache: ${cacheKey}`);
  
  // Verificar cache
  const cachedData = queryClient.getQueryData(cacheKey);
  
  if (cachedData) {
    console.log(`[fetchAvailableTimeSlotsWithCache] Usando dados do cache, slots: ${cachedData.length}`);
    return cachedData;
  }
  
  // Se não está em cache, buscar do servidor
  console.log(`[fetchAvailableTimeSlotsWithCache] Buscando do servidor com excludeAppointmentId=${excludeAppointmentId}`);
  
  try {
    const slots = await getAvailableTimeSlots(
      salonId,
      date,
      professionalId,
      serviceIds,
      excludeAppointmentId
    );
    
    console.log(`[fetchAvailableTimeSlotsWithCache] Slots retornados do servidor: ${slots.length}, excludeAppointmentId=${excludeAppointmentId}`);
    
    // Salvar no cache
    queryClient.setQueryData(cacheKey, slots);
    
    return slots;
  } catch (error) {
    console.error(`[fetchAvailableTimeSlotsWithCache] Erro ao buscar slots:`, error);
    return [];
  }
};

/**
 * Hook para obter todos os serviços com cache
 * @param {string} salonId - ID do salão
 * @returns {object} - Resultado da query com cache
 */
export const useAllServices = (salonId) => {
  return useQuery({
    queryKey: queryKeys.services.all(salonId),
    queryFn: () => getAllServices(salonId),
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 30 * 60 * 1000, // 30 minutos
  });
};

/**
 * Hook para obter todos os profissionais com cache
 * @param {string} salonId - ID do salão 
 * @returns {object} - Resultado da query com cache
 */
export const useAllProfessionals = (salonId) => {
  return useQuery({
    queryKey: queryKeys.professionals.all(salonId),
    queryFn: () => getAllProfessionals(salonId),
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 30 * 60 * 1000, // 30 minutos
  });
}; 